// Trainer outfits on the client: the outfit list on the Trainer page (wear and
// preview), the Outfits section of the diamond shop, and the small portrait
// beside a trainer's name in chat and on the leaderboards. The server checks
// ownership; this only draws and sends.
import { OUTFITS, OUTFIT, outfitOf, ownsOutfit, outfitHow, type Outfit } from '../shared/outfits.ts';
import { REGIONS } from '../shared/grounds.ts';
import type { ClientMsg } from '../shared/protocol.ts';
import { S } from './state.ts';
import { spriteUrl, iconUrl } from './assets.ts';
import { esc } from './fmt.ts';
import { confirmClick, confirmLabel, priceLabel, isArmed } from './confirm.ts';
import { trainerSprite, mySprite } from './trainerart.ts';
import { lookDraft, lookPickerClick, lookSave } from './lookpicker.ts';

let send: (m: ClientMsg) => void = () => {};
let rerender: () => void = () => {};
export function bindOutfits(o: { send: (m: ClientMsg) => void; render: () => void }) { send = o.send; rerender = o.render; }

/** Outfit being previewed on the Trainer page (not worn); empty for none. */
let preview = '';

/** The outfit the Trainer page shows: the preview if one is picked, else the one worn. */
export function shownOutfit(): Outfit {
  return preview && OUTFIT[preview] ? OUTFIT[preview] : outfitOf(S.trainer?.outfit);
}

/** A small head-and-shoulders crop of an outfit or worn code (with the trainer's look), for chat and board rows. */
export function trainerFace(outfit: string | undefined): string {
  return `<span class="tface" style="background-image:url('${spriteUrl(`${trainerSprite(outfit)}-down-0`)}')" aria-hidden="true"></span>`;
}

const regionName = (o: Outfit) => (o.region ? REGIONS.find((r) => r.id === o.region)?.name : '') ?? '';

function status(o: Outfit) {
  const t = S.trainer!;
  const wearing = outfitOf(t.outfit).id === o.id;
  const own = ownsOutfit(t, o.id);
  return { wearing, own };
}

/** One outfit card. On the Trainer page it previews and wears; in the shop it buys. */
function card(o: Outfit, where: 'trainer' | 'shop') {
  const t = S.trainer!;
  const { wearing, own } = status(o);
  const price = o.source.kind === 'shop' ? o.source.price : 0;
  const pills = [
    wearing ? '<span class="pill lead">Wearing</span>' : own ? '<span class="pill good">Owned</span>' : '',
    regionName(o) ? `<span class="pill">${esc(regionName(o))}</span>` : '',
    where === 'trainer' && preview === o.id && !wearing ? '<span class="pill amber">Previewing</span>' : '',
  ].join('');
  const buttons: string[] = [];
  if (own && !wearing) buttons.push(`<button class="btn small primary" data-act="outfit-wear" data-id="${o.id}">Wear</button>`);
  if (where === 'trainer' && !wearing) buttons.push(`<button class="btn small" data-act="outfit-preview" data-id="${o.id}">${preview === o.id ? 'Stop preview' : 'Preview'}</button>`);
  if (!own && price) {
    buttons.push(where === 'shop'
      ? `<button class="btn small gold${isArmed(`outfit:${o.id}`) ? ' armed' : ''}" data-act="outfit-buy" data-id="${o.id}"${t.diamonds < price ? ' disabled title="Not enough diamonds"' : ''}>${priceLabel(`outfit:${o.id}`, `Buy · ${price} ◆`, price)}</button>`
      : `<a class="btn small" href="#/shop/diamonds">In the diamond shop</a>`);
  }
  const how = own ? (o.source.kind === 'shop' ? (t.devOutfits?.includes(o.id) ? 'Granted by an admin' : 'Bought') : outfitHow(o)) : outfitHow(o);
  return `<div class="ofcard${wearing ? ' on' : ''}${own ? '' : ' locked'}" data-key="of-${o.id}">
    <div class="of-pic"><img src="${spriteUrl(`${mySprite(o.id)}-down-0`)}" alt=""></div>
    <div class="grow"><div class="row between"><span><b class="nm">${esc(o.name)}</b> ${pills}</span></div>
      <p>${esc(o.desc)}</p>${!own && price ? '' : `<p class="muted of-how">${esc(how)}</p>`}
      ${buttons.length ? `<div class="buyrow">${buttons.join('')}</div>` : ''}</div></div>`;
}

/** Every outfit as a card for the player screen's Outfits tab: wear, preview, or where to get it. */
export function outfitCards(): string { return OUTFITS.map((x) => card(x, 'trainer')).join(''); }

/** The Outfit panel on the Trainer page: every outfit, with what each takes. */
export function trainerOutfits(): string {
  const o = shownOutfit();
  const owned = OUTFITS.filter((x) => ownsOutfit(S.trainer!, x.id)).length;
  return `<section class="panel"><div class="row between"><h4>Outfits</h4><span class="muted">${owned} of ${OUTFITS.length} owned</span></div>
    <div class="tp-outfit"><div class="tp-dirs">${['down', 'left', 'up', 'right'].map((d) => `<img src="${spriteUrl(`${mySprite(o.id)}-${d}-0`)}" alt="">`).join('')}</div>
      </div>
    <div class="ofgrid">${OUTFITS.map((x) => card(x, 'trainer')).join('')}</div>
  </section>`;
}

/** The Outfits section of the diamond shop. */
export function shopOutfits(): string {
  const sold = OUTFITS.filter((o) => o.source.kind === 'shop');
  return `<div class="ofgrid" data-fit="ds-outfits">${sold.map((o) => card(o, 'shop')).join('')}</div>`;
}

export function outfitsClick(el: HTMLElement): boolean {
  const id = el.dataset.id ?? '';
  // A live look picker (the Outfits tab) saves each change; a draft picker (the tutorial's) is left to its owner.
  if (el.closest('.lookpk[data-live]')) {
    const d = lookDraft();
    // Swapping which side the big preview shows changes nothing to save.
    if (el.dataset.act === 'look-view') { lookPickerClick(d, el); rerender(); return true; }
    if (lookPickerClick(d, el)) { lookSave(send, d); return true; }
  }
  switch (el.dataset.act) {
    case 'outfit-wear': preview = ''; send({ t: 'outfitWear', id }); return true;
    // Diamond purchases take a second click on the same button (inline, no dialog).
    case 'outfit-buy': if (confirmClick(`outfit:${id}`)) { preview = ''; send({ t: 'outfitBuy', id }); } return true;
    case 'outfit-preview': preview = preview === id ? '' : id; rerender(); scrollTo0(); return true;
    default: return false;
  }
}

/** Bring the large sprite back into view after picking a preview lower on the page. */
function scrollTo0() { document.querySelector('.tp-hero, .ph-id')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }

/** Forget the preview when leaving the Trainer page. */
export function clearPreview() { preview = ''; }
